import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "../../AxiosUrl";

const getUid = () => localStorage.getItem("id");

// Campaign API hooks
export const useCampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const fetch = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const res = await axios.post(`/campaign/list/${getUid()}`, {
        page: params.page || 1,
        limit: params.limit || 12,
        status: params.status || undefined,
        type: params.type || undefined,
        search: params.search || undefined
      });
      if (res.data.success) {
        setCampaigns(res.data.data);
        setPagination({
          page: res.data.pagination.page,
          totalPages: res.data.pagination.totalPages,
          total: res.data.pagination.totalCampaigns
        });
      }
    } catch (e) {
      toast.error("Error fetching campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  return { campaigns, loading, pagination, fetch };
};

export const useCampaignDetails = () => {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await axios.get(`/campaign/${id}/${getUid()}`);
      if (res.data.success) setCampaign(res.data.data);
      return res.data;
    } catch (e) {
      toast.error("Error fetching campaign");
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  return { campaign, loading, fetch, setCampaign };
};

export const useCampaignActions = (onSuccess) => {
  const execute = async (id) => {
    if (!window.confirm("Execute this campaign?")) return;
    try {
      const res = await axios.post(`/campaign/execute/${id}/${getUid()}`);
      res.data.success ? toast.success(res.data.message || "Campaign executed!") : toast.error(res.data.error);
      if (res.data.success && onSuccess) onSuccess();
    } catch (e) {
      toast.error("Error executing campaign");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this campaign?")) return;
    try {
      const res = await axios.delete(`/campaign/delete/${id}/${getUid()}`);
      res.data.success ? toast.success("Campaign deleted!") : toast.error(res.data.error);
      if (res.data.success && onSuccess) onSuccess();
      return res.data.success;
    } catch (e) {
      toast.error("Error deleting campaign");
      return false;
    }
  };

  const save = async (data, isEdit = false) => {
    try {
      const payload = {
        ...data,
        createdBy: getUid(),
        createdByName: localStorage.getItem("adminName") || "Admin",
        ...(isEdit && { lastModifiedBy: getUid() })
      };
      // The backend route for edits is PUT /campaign/update
      const res = isEdit
        ? await axios.put(`/campaign/update/${getUid()}`, payload)
        : await axios.post(`/campaign/create/${getUid()}`, payload);
      res.data.success
        ? toast.success(isEdit ? "Campaign updated!" : "Campaign created!")
        : toast.error(res.data.error);
      return res.data.success;
    } catch (e) {
      toast.error("Error saving campaign");
      return false;
    }
  };

  return { execute, remove, save };
};

// SMS results: cost, payers, revenue, sent vs holdout
export const useCampaignReport = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async (id, channel) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/campaign/report/${id}/${getUid()}`, { params: channel ? { channel } : {} });
      if (res.data.success) setReport(res.data.data);
      else setError(res.data.error || "Could not load results");
    } catch (e) {
      setError("Could not load results");
    } finally {
      setLoading(false);
    }
  }, []);

  return { report, loading, error, fetch };
};

export const MARKETING_DEFAULTS = {
  costs: { smsRatePerPart: 0.35, callRatePerMinute: 0.45, usdToBdt: 122 },
  attributionDays: 7,
  holdoutPercent: 10,
  reminderHoldoutPercent: 0,
  shortLinkStyle: "full",
  timing: {
    promoSmsCap: { count: 2, days: 7 },
    quietHours: { start: "23:00", end: "08:00" },
    sendWindows: [{ start: "12:00", end: "13:00" }, { start: "21:00", end: "22:00" }],
    preferredDays: [3, 0],
    reminders: { incompleteProfile: "12:00", unreadNotifications: "18:00", newProfilesDay: 0, newProfilesTime: "21:00" },
    abandonedReminder: { delayMinutes: 60, minGapHours: 6 },
    ladder: { pushAfterMinutes: 30, smsAfterMinutes: 120, callNextDay: true, minAmount: 500, smsText: "" }
  },
  optOut: { respectPromotionalSms: true, respectPushSwitchForSms: true },
  noApp: { android: "playstore" },
  features: { smsTracking: true, holdout: true, pushMeasurement: true, sendTimeAB: true, autoDailySends: false, channelLadder: false },
  budgets: { smsDailyBdt: 0 },
  access: {
    marketingSettings: ["Supper", "Admin"],
    campaigns: ["Supper", "Admin", "Team"],
    aiCalling: ["Supper", "Admin"],
    sipLine: ["Supper"],
    recordings: ["Supper"]
  }
};

export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Next few suggested send times (Bangladesh) from the send windows and preferred days. */
export const suggestedSlots = (settings, count = 4) => {
  const windows = settings?.timing?.sendWindows || MARKETING_DEFAULTS.timing.sendWindows;
  const days = settings?.timing?.preferredDays || MARKETING_DEFAULTS.timing.preferredDays;
  const out = [];
  const now = Date.now();
  const BD = 6 * 3600 * 1000;
  for (let d = 0; d < 14 && out.length < count; d++) {
    const dayStart = Math.floor((now + BD) / 86400000) * 86400000 + d * 86400000; // BD midnight, shifted
    const weekday = new Date(dayStart).getUTCDay();
    if (!days.includes(weekday)) continue;
    for (const w of windows) {
      const [h, m] = w.start.split(":").map(Number);
      const at = dayStart + (h * 60 + m) * 60000 - BD;
      if (at > now + 5 * 60000) out.push(new Date(at));
      if (out.length >= count) break;
    }
  }
  return out;
};

/** "YYYY-MM-DDTHH:MM" in Bangladesh time, for <input type="datetime-local"> */
export const toBdInput = (date) => (date ? new Date(new Date(date).getTime() + 6 * 3600 * 1000).toISOString().slice(0, 16) : "");
/** Back from the input (Bangladesh time) to a Date */
export const fromBdInput = (value) => (value ? new Date(`${value}:00+06:00`) : null);

// Audience preset size (item 30)
export const usePresetPreview = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fetch = useCallback(async (key, options) => {
    if (!key) return setPreview(null);
    setLoading(true);
    try {
      const res = await axios.post(`/marketing/preset-preview/${getUid()}`, { key, options });
      setPreview(res.data.success ? res.data.data : null);
    } catch (e) {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);
  return { preview, loading, fetch };
};

// Simple GET hook for the marketing report pages
export const useMarketingGet = (path) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fetch = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${path}/${getUid()}`, { params });
      if (res.data.success) setData(res.data.data);
      else setError(res.data.error || "Could not load");
    } catch (e) {
      setError(e.response?.data?.msg || "Could not load");
    } finally {
      setLoading(false);
    }
  }, [path]);
  return { data, loading, error, fetch };
};

// Automations (item 31)
export const useAutomations = () => {
  const [automations, setAutomations] = useState([]);
  const [featureOn, setFeatureOn] = useState(false);
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/marketing/automations/${getUid()}`);
      if (res.data.success) {
        setAutomations(res.data.data);
        setFeatureOn(!!res.data.featureOn);
      }
    } catch (e) {
      toast.error("Error loading automations");
    } finally {
      setLoading(false);
    }
  }, []);
  const call = async (method, url, body, okMessage) => {
    try {
      const res = await axios({ method, url, data: body });
      if (res.data.success) {
        toast.success(res.data.message || okMessage);
        await fetch();
      } else toast.error(res.data.error);
      return res.data;
    } catch (e) {
      toast.error(e.response?.data?.msg || "Request failed");
      return { success: false };
    }
  };
  return {
    automations,
    featureOn,
    loading,
    fetch,
    create: (automation) => call("post", `/marketing/automations/${getUid()}`, { automation }, "Created"),
    update: (id, automation) => call("put", `/marketing/automations/${id}/${getUid()}`, { automation }, "Saved"),
    remove: (id) => call("delete", `/marketing/automations/${id}/${getUid()}`, undefined, "Deleted"),
    run: (id) => call("post", `/marketing/automations/run/${id}/${getUid()}`, {}, "Sent")
  };
};

// Marketing settings (prices, attribution window, holdout, link style, access)
export const useMarketingSettings = () => {
  const [settings, setSettings] = useState(null);
  const [permissions, setPermissions] = useState({ canEdit: false, canEditAccess: false, can: {} });
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/marketing/settings/${getUid()}`);
      if (res.data.success) {
        setSettings(res.data.data);
        setPermissions({ canEdit: !!res.data.canEdit, canEditAccess: !!res.data.canEditAccess, can: res.data.can || {} });
        setPresets(res.data.presets || []);
        return res.data.data;
      }
      return null;
    } catch (e) {
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const save = async (data) => {
    try {
      const res = await axios.put(`/marketing/settings/${getUid()}`, data);
      if (res.data.success) {
        toast.success(res.data.message || "Saved");
        await fetch();
      } else {
        toast.error(res.data.error);
      }
      return res.data.success;
    } catch (e) {
      toast.error("Error saving marketing settings");
      return false;
    }
  };

  return { settings, permissions, presets, loading, fetch, save };
};

// Utility functions
export const calcRate = (num, den) => (den ? ((num / den) * 100).toFixed(1) : 0);

export const statusColors = {
  draft: "default", scheduled: "info", running: "warning",
  paused: "secondary", completed: "success", failed: "error"
};

export const typeColors = { push: "primary", sms: "secondary", both: "info" };

export const defaultFormData = {
  name: "", description: "", type: "both",
  targetAudience: {
    filterType: "all",
    customFilters: {
      gender: "all", ageRange: { min: 18, max: 60 },
      subscriptionStatus: [], lastActiveWithin: null,
      hasExpoToken: false, hasMobile: false,
      profileCompleted: false, hasPhoto: false, hasCV: false
    },
    selectedUserIds: [], estimatedReach: 0,
    preset: { key: "abandonedCheckout", options: {} }
  },
  pushMessage: {
    title: "", body: "", priority: "normal", redirectUrl: "",
    redirectUrlWeb: "https://app.bibahobangla.com/dashboard",
    redirectUrlMobile: "bibahobangla://dashboard"
  },
  smsMessage: {
    content: "", redirectUrl: "", landing: "dashboard",
    redirectUrlWeb: "https://app.bibahobangla.com/dashboard",
    redirectUrlMobile: "bibahobangla://dashboard"
  },
  schedule: { type: "immediate", sendAt: null },
  abTest: { enabled: false, slotA: "12:00", slotB: "21:00" },
  perUserBestHour: false,
  // Filled from Marketing settings when the form opens
  holdoutPercent: MARKETING_DEFAULTS.holdoutPercent,
  attributionDays: MARKETING_DEFAULTS.attributionDays
};

// Holdout only applies to single-channel campaigns (SMS or push) sent to a filtered audience:
// a hand-picked list must all get it, and in "both" the holdout would still get the other channel.
export const holdoutApplies = (form) => form.type !== "both" && form.targetAudience?.filterType !== "selected";

export const validateForm = (data) => {
  if (!data.name.trim()) return "Campaign name is required";
  if ((data.type === "push" || data.type === "both") &&
      (!data.pushMessage.title.trim() || !data.pushMessage.body.trim())) {
    return "Push notification title and body are required";
  }
  if ((data.type === "sms" || data.type === "both") && !data.smsMessage.content.trim()) {
    return "SMS content is required";
  }
  if (data.smsMessage.content.length > 160) return "SMS must be 160 characters or less";
  if (data.targetAudience.filterType === "selected" &&
      (!data.targetAudience.selectedUserIds?.length)) {
    return "Please select at least one user";
  }
  const holdout = Number(data.holdoutPercent);
  if (holdoutApplies(data) && (!Number.isFinite(holdout) || holdout < 0 || holdout > 50)) {
    return "Holdout must be between 0 and 50%";
  }
  const days = Number(data.attributionDays);
  if (!Number.isInteger(days) || days < 1 || days > 30) return "Attribution window must be 1–30 days";
  if (data.targetAudience.filterType === "preset" && !data.targetAudience.preset?.key) return "Pick an audience preset";
  if (data.schedule?.type === "scheduled") {
    if (!data.schedule.sendAt) return "Pick a send time";
    if (new Date(data.schedule.sendAt) <= new Date()) return "The send time must be in the future";
  }
  if (data.abTest?.enabled && (data.type !== "sms" || data.abTest.slotA === data.abTest.slotB)) {
    return "The A/B test needs an SMS-only campaign and two different times";
  }
  return null;
};
